/**
 * ScrollNavBlock — Sticky anchor navigation with IntersectionObserver scroll-spy.
 *
 * Renders a horizontal nav bar with links to target content blocks on the page.
 * Uses IntersectionObserver to highlight the currently visible section.
 * When a nav item is clicked, the page smoothly scrolls to the target block.
 *
 * Styles: "cards" (gradient card), "tabs" (underline), "pills" (filled rounded), "minimal" (plain text).
 * Sticky mode uses position: sticky to keep the nav visible during scroll.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import DOMPurify from "dompurify";
import "./ScrollNavBlock.scss";

interface ScrollNavItem {
    _id: string;
    label: string;
    targetBlockId?: string;
    action?: "scroll" | "modal";
    modalHtmlContent?: string;
}

interface ScrollNavBlockProps {
    config?: {
        items?: ScrollNavItem[];
        sticky?: boolean;
        stickyTopOffset?: number;
        style?: "cards" | "tabs" | "pills" | "minimal";
        alignment?: "left" | "center" | "right";
    };
    sectionTitle?: string;
}

const VALID_STYLES = new Set(["cards", "tabs", "pills", "minimal"]);

/** Only allow alphanumeric, hyphens, and underscores in block IDs used in querySelector */
const SAFE_BLOCK_ID_RE = /^[a-zA-Z0-9_-]{1,100}$/;

export const ScrollNavBlock = ({ config, sectionTitle }: ScrollNavBlockProps) => {
    const items = useMemo(() => config?.items || [], [config?.items]);
    const style = config?.style && VALID_STYLES.has(config.style) ? config.style : "pills";
    const isSticky = config?.sticky !== false;
    const stickyTopOffset = config?.stickyTopOffset;
    const alignment = config?.alignment || "left";
    const [activeId, setActiveId] = useState<string | null>(null);
    const [modalContent, setModalContent] = useState<{ label: string; html: string } | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);

    /**
     * Set up IntersectionObserver to track which target blocks are in the viewport.
     * The most recently intersecting block becomes the active nav item.
     */
    useEffect(() => {
        if (items.length === 0) return;

        const targetIds = items
            .map(item => item.targetBlockId)
            .filter((id): id is string => Boolean(id) && SAFE_BLOCK_ID_RE.test(id!));

        if (targetIds.length === 0) return;

        // Clean up previous observer
        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        const visibleEntries = new Map<string, boolean>();

        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    const blockId = entry.target.getAttribute("data-block-id") || "";
                    visibleEntries.set(blockId, entry.isIntersecting);
                });

                // Find the first visible target in DOM order
                let found = false;
                for (const id of targetIds) {
                    if (visibleEntries.get(id)) {
                        setActiveId(id);
                        found = true;
                        break;
                    }
                }
                if (!found) {
                    setActiveId(null);
                }
            },
            { threshold: 0.2, rootMargin: "-80px 0px -40% 0px" },
        );

        // Observe all target elements (blocks render with data-block-id)
        targetIds.forEach(id => {
            const el = document.querySelector(`[data-block-id="${id}"]`);
            if (el) {
                observerRef.current?.observe(el);
            }
        });

        return () => {
            observerRef.current?.disconnect();
        };
    }, [items]);

    const handleClick = useCallback((item: ScrollNavItem) => {
        if (item.action === "modal" && item.modalHtmlContent) {
            setModalContent({ label: item.label, html: item.modalHtmlContent });
            return;
        }
        const targetBlockId = item.targetBlockId;
        if (!targetBlockId || !SAFE_BLOCK_ID_RE.test(targetBlockId)) return;
        const el = document.querySelector(`[data-block-id="${targetBlockId}"]`);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, []);

    if (items.length === 0) return null;

    // Inline style for configurable sticky offset and alignment
    const navStyle: React.CSSProperties = {
        ...isSticky && stickyTopOffset != null ? { top: `${stickyTopOffset}px` } : {},
    };

    const itemsStyle: React.CSSProperties = {
        ...alignment !== "left" ? { justifyContent: alignment === "center" ? "center" : "flex-end" } : {},
    };

    return (
        <nav
            className={`scroll-nav scroll-nav--${style} ${isSticky ? "scroll-nav--sticky" : ""}`}
            style={navStyle}
        >
            {sectionTitle && <span className="scroll-nav__title">{sectionTitle}</span>}
            <div className="scroll-nav__items" style={itemsStyle}>
                {items.map(item =>
                    <button
                        key={item._id}
                        className={`scroll-nav__item ${activeId === item.targetBlockId ? "scroll-nav__item--active" : ""}`}
                        onClick={() => handleClick(item)}
                        type="button"
                    >
                        {item.label}
                    </button>)}
            </div>

            {modalContent &&
                <div
                    className="scroll-nav__modal-overlay"
                    onClick={() => setModalContent(null)}
                    role="dialog"
                    aria-label={modalContent.label}
                >
                    <div
                        className="scroll-nav__modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="scroll-nav__modal-header">
                            <h3>{modalContent.label}</h3>
                            <button
                                type="button"
                                className="scroll-nav__modal-close"
                                onClick={() => setModalContent(null)}
                                aria-label="Close"
                            >
                                &times;
                            </button>
                        </div>
                        <div
                            className="scroll-nav__modal-body"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(modalContent.html) }}
                        />
                    </div>
                </div>
            }
        </nav>
    );
};
